import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  getInterviewHistory,
  scheduleInterview as scheduleInterviewApi,
  rescheduleInterview as rescheduleInterviewApi,
  startInterview as startInterviewApi,
  cancelInterview as cancelInterviewApi,
  noShowInterview as noShowInterviewApi,
  completeInterview as completeInterviewApi,
} from '@/modules/hrPortal/api/interview.api';

import type {
  Interview,
  ScheduleInterviewPayload,
  RescheduleInterviewPayload,
} from '@/modules/hrPortal/types/interview.types';

export const fetchInterviewHistory = createAsyncThunk<
  Interview[],
  string,
  { rejectValue: string }
>(
  'interview/fetchInterviewHistory',
  async (applicationId, thunkAPI) => {
    try {
      const response = await getInterviewHistory(applicationId);

      const body: any = response.data;

      if (body?.success === false) {
        return thunkAPI.rejectWithValue(
          body?.message || 'Unable to fetch interview history.'
        );
      }

      return body?.data ?? body;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          'Unable to fetch interview history.'
      );
    }
  }
);

export const scheduleInterview = createAsyncThunk<
  Interview,
  ScheduleInterviewPayload,
  { rejectValue: string }
>(
  'interview/scheduleInterview',
  async (payload, thunkAPI) => {
    try {
      const response = await scheduleInterviewApi(payload);

      const body: any = response.data;

      if (body?.success === false) {
        return thunkAPI.rejectWithValue(
          body?.message || 'Unable to schedule interview.'
        );
      }

      return body?.data ?? body;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          'Unable to schedule interview.'
      );
    }
  }
);

export const rescheduleInterview = createAsyncThunk<
  Interview,
  {
    interviewId: string;
    payload: RescheduleInterviewPayload;
  },
  { rejectValue: string }
>(
  'interview/rescheduleInterview',
  async ({ interviewId, payload }, thunkAPI) => {
    try {
      const response = await rescheduleInterviewApi(
        interviewId,
        payload
      );

      const body: any = response.data;

      if (body?.success === false) {
        return thunkAPI.rejectWithValue(
          body?.message || 'Unable to reschedule interview.'
        );
      }

      return body?.data ?? body;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          'Unable to reschedule interview.'
      );
    }
  }
);

export const startInterview = createAsyncThunk<
  Interview,
  string,
  { rejectValue: string }
>(
  'interview/startInterview',
  async (interviewId, thunkAPI) => {
    try {
      const response = await startInterviewApi(interviewId);

      const body: any = response.data;

      if (body?.success === false) {
        return thunkAPI.rejectWithValue(
          body?.message || 'Unable to start interview.'
        );
      }

      return body?.data ?? body;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          'Unable to start interview.'
      );
    }
  }
);

export const cancelInterview = createAsyncThunk<
  Interview,
  {
    interviewId: string;
    reason: string;
  },
  { rejectValue: string }
>(
  'interview/cancelInterview',
  async ({ interviewId, reason }, thunkAPI) => {
    try {
      const response = await cancelInterviewApi(
        interviewId,
        reason
      );

      const body: any = response.data;

      if (body?.success === false) {
        return thunkAPI.rejectWithValue(
          body?.message || 'Unable to cancel interview.'
        );
      }

      return body?.data ?? body;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          'Unable to cancel interview.'
      );
    }
  }
);

export const noShowInterview = createAsyncThunk<
  Interview,
  {
    interviewId: string;
    reason: string;
  },
  { rejectValue: string }
>(
  'interview/noShowInterview',
  async ({ interviewId, reason }, thunkAPI) => {
    try {
      const response = await noShowInterviewApi(
        interviewId,
        reason
      );

      const body: any = response.data;

      if (body?.success === false) {
        return thunkAPI.rejectWithValue(
          body?.message || 'Unable to mark interview as no-show.'
        );
      }

      return body?.data ?? body;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          'Unable to mark interview as no-show.'
      );
    }
  }
);

export const completeInterview = createAsyncThunk<
  Interview,
  {
    interviewId: string;
    result: 'PASSED' | 'FAILED';
    remarks?: string;
  },
  { rejectValue: string }
>(
  'interview/completeInterview',
  async ({ interviewId, result, remarks }, thunkAPI) => {
    try {
      const response = await completeInterviewApi(
        interviewId,
        result,
        remarks
      );

      const body: any = response.data;

      if (body?.success === false) {
        return thunkAPI.rejectWithValue(
          body?.message || 'Unable to complete interview.'
        );
      }

      return body?.data ?? body;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          'Unable to complete interview.'
      );
    }
  }
);